/**
 * Reduce File Size: three levels, and what each one did.
 */

import { useState } from 'react'
import { api, type CompressLevel, type CompressReport, type SessionInfo } from '../api'

interface Props {
  sessionId: string
  onDone: (session: SessionInfo) => void
  onClose: () => void
}

const LEVELS: { id: CompressLevel; label: string; detail: string }[] = [
  { id: 'lossless', label: 'Lossless',
    detail: 'Nothing you can see changes. Duplicate images, leftover app data and unused parts are removed, and the file is packed tighter.' },
  { id: 'standard', label: 'Standard',
    detail: 'Also reduces photos to 150 dpi at the size they are shown — sharp on screen and in office printing.' },
  { id: 'small', label: 'Smallest',
    detail: 'Photos at 100 dpi, lower quality. For email limits; fine print in photos may soften.' },
]

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function CompressDialog({ sessionId, onDone, onClose }: Props) {
  const [level, setLevel] = useState<CompressLevel>('standard')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [report, setReport] = useState<CompressReport | null>(null)

  const run = async () => {
    setBusy(true)
    setError('')
    try {
      const r = await api.compress(sessionId, level)
      setReport(r)
      onDone(r.session)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const fraction = report && report.before ? 1 - report.after / report.before : 0
  // "100% smaller" read as if nothing was left; say more than 99 instead.
  const saved = fraction > 0.995 ? '>99' : Math.max(0, Math.round(fraction * 100))
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="compress-title">
      <div className="modal__panel">
        <h2 className="modal__title" id="compress-title">Reduce file size</h2>
        <div className="modal__body">
          {report ? (
            <>
              <p className="compress__result">
                {formatSize(report.before)} &rarr; <strong>{formatSize(report.after)}</strong>
                {saved === '>99' || (saved as number) > 0 ? ` (${saved}% smaller)` : ' (already about as small as it gets)'}
              </p>
              <ul className="compress__list">
                {report.imagesDownsampled > 0 && <li>{report.imagesDownsampled} image(s) reduced</li>}
                {report.imagesMerged > 0 && <li>{report.imagesMerged} duplicate image(s) merged</li>}
                {report.removed.map((item) => <li key={item}>Removed {item}</li>)}
                <li>Packed for saving</li>
              </ul>
              <p className="modal__hint">Save to keep it. Undo (Ctrl+Z) puts the images back.</p>
            </>
          ) : (
            LEVELS.map((option) => (
              <label className="check compress__level" key={option.id}>
                <input type="radio" name="compress-level" checked={level === option.id}
                       onChange={() => setLevel(option.id)} />
                <span><strong>{option.label}</strong><br /><span className="modal__hint">{option.detail}</span></span>
              </label>
            ))
          )}
          {error && <p className="modal__hint modal__hint--warn">{error}</p>}
        </div>
        <div className="modal__footer">
          {report ? (
            <button className="btn btn--primary" onClick={onClose}>Done</button>
          ) : (
            <>
              <button className="btn" onClick={onClose}>Cancel</button>
              <button className="btn btn--primary" disabled={busy} onClick={() => void run()}>
                {busy ? 'Reducing…' : 'Reduce'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
